import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../../services/auth.service';

/** One label/value line of evidence, already worded and compared by the server. */
export interface AgentFact {
  label: string;
  value: string;
  /** null | 'good' | 'warn' | 'bad' — a rendering hint, never a second meaning. */
  tone: string | null;
}

export interface AgentRecommendation {
  kind: string;
  leadId: number | null;
  /** The id this card's own action needs: link, approval request, or quote. */
  referenceId: number | null;
  title: string;
  subtitle: string | null;
  reason: string;
  contributionBase: number | null;
  score: number | null;
  routeFitStatus: string | null;
  sinceUtc: string | null;
  facts: AgentFact[];
}
export interface AgentQueue { total: number; items: AgentRecommendation[]; }

/** What a card asks a manager to do, and whether the server needs a reason. */
type Decision =
  | { verb: 'merge'; needsReason: false }
  | { verb: 'split'; needsReason: false }
  | { verb: 'approveQuote'; needsReason: true }
  | { verb: 'sendBackQuote'; needsReason: true };

/**
 * The agent work queue: instead of a table of 3,000 rows waiting to be read,
 * the agent sorts and pushes up only what needs a human decision - each card
 * carrying the evidence it used. Acting on a card is a real command (taking a
 * recommended lead assigns it to you, merging folds one Lead into another, and
 * approving an exception quote moves it to Approved); the rest route to the
 * surface that owns the decision.
 *
 * The one card that deliberately has no decide button is Approval: approving
 * an approval_request records the decision and mints an execution token, but
 * nothing in this system spends that token yet, so a button here would say
 * "approved" while nothing moved. That card links to the approval center,
 * where the payload can be read verbatim, and its own evidence says so.
 */
@Component({
  selector: 'app-agent-queue',
  templateUrl: './agent-queue.component.html',
  styleUrls: ['./agent-queue.component.css'],
})
export class AgentQueueComponent implements OnInit {
  /** Raised after a card is acted on, so the host can refresh its own data. */
  @Output() acted = new EventEmitter<void>();

  /** Raised with every load, so a host can headline the same counts without
   *  fetching the queue a second time. */
  @Output() loaded = new EventEmitter<AgentQueue>();

  readonly kindLabels: Record<string, string> = {
    StartPursuit: 'Recommended to pursue',
    IdentityReview: 'Same site?',
    Approval: 'Awaiting approval',
    GuardrailWarning: 'Profit guardrail',
    QuoteException: 'Exception quote',
  };

  /** Chip order = the three decisions the Overview exists for, then the rest. */
  readonly filterKinds: readonly { kind: string; label: string; css: string }[] = [
    { kind: 'StartPursuit', label: 'Pursue', css: 'aq-chip-go' },
    { kind: 'QuoteException', label: 'Exception quotes', css: 'aq-chip-crit' },
    { kind: 'IdentityReview', label: 'Same site', css: 'aq-chip-rev' },
    { kind: 'Approval', label: 'Approvals', css: 'aq-chip-ag' },
    { kind: 'GuardrailWarning', label: 'Guardrail', css: 'aq-chip-crit' },
  ];

  queue: AgentQueue | null = null;
  loading = false;
  loadError: string | null = null;
  /** Card keys currently being acted on, so a card cannot double-fire. */
  busy = new Set<string>();
  actionError: string | null = null;

  /** Client-side kind filter; null = everything. */
  kindFilter: string | null = null;

  /** The card whose reason box is open, and what it is about to do. */
  pending: { key: string; decision: Decision } | null = null;
  reason = '';

  /** Who "take it" assigns to. Null when the token does not say, which is the
   *  one state in which the button cannot do what it says. */
  private myUserId: number | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    // Resolve the acting user from the shared frontend tenant context.
    this.myUserId = this.auth.tenant.currentUserId;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.http.get<AgentQueue>('/api/leads-agent/queue').subscribe({
      next: res => { this.queue = res; this.loading = false; this.loaded.emit(res); },
      error: () => {
        this.loadError = 'Failed to load the agent queue.';
        this.loading = false;
      },
    });
  }

  get items(): AgentRecommendation[] {
    const all = this.queue?.items ?? [];
    return this.kindFilter ? all.filter(i => i.kind === this.kindFilter) : all;
  }

  countOf(kind: string): number {
    return (this.queue?.items ?? []).filter(i => i.kind === kind).length;
  }

  kindLabel(kind: string): string {
    return this.kindLabels[kind] ?? kind;
  }

  setFilter(kind: string | null): void {
    this.kindFilter = this.kindFilter === kind ? null : kind;
  }

  /** Kind + reference identifies a card across a reload. */
  cardKey(card: AgentRecommendation): string {
    return `${card.kind}:${card.referenceId ?? card.leadId ?? 0}`;
  }

  isBusy(card: AgentRecommendation): boolean {
    return this.busy.has(this.cardKey(card));
  }

  // ── Decisions that need a reason ────────────────────────────────────────
  // The server refuses a quote decision without one, so the card asks for it
  // rather than sending a placeholder the audit trail would have to live with.

  ask(card: AgentRecommendation, decision: Decision): void {
    this.actionError = null;
    this.reason = '';
    this.pending = { key: this.cardKey(card), decision };
  }

  cancelAsk(): void {
    this.pending = null;
    this.reason = '';
  }

  asking(card: AgentRecommendation): Decision | null {
    return this.pending?.key === this.cardKey(card) ? this.pending.decision : null;
  }

  // ── The commands ───────────────────────────────────────────────────────

  /**
   * Take it: accepting the recommendation ASSIGNS the lead to whoever clicked,
   * and leaves the stage exactly where it is.
   *
   * It used to move the lead into Contacted, a stage that no longer exists
   * (db/changes/2026-08-22--lead-stage-vocabulary.sql), so the button would
   * have looked up an id that was never there and returned without a word.
   * Assignment is also the truer reading of the card: the agent is not
   * claiming the lead has been worked, it is asking someone to own it.
   * Whether it has been presented to is for that person to say, on the Board.
   */
  takeIt(card: AgentRecommendation): void {
    if (card.leadId == null) return;
    if (this.myUserId == null) {
      // No id, no assignment - and say so, rather than let a click do nothing.
      this.actionError =
        'Your sign-in does not say who you are, so this lead cannot be '
        + 'assigned to you. Sign in again and retry.';
      return;
    }
    this.run(card, () => this.http.put(`/api/lead-pipeline/${card.leadId}`, {
      salesRepUserId: this.myUserId,
    }), {
      403: 'You do not have permission to take leads.',
      404: 'That lead is no longer on this account.',
      fallback: 'Could not take this lead.',
    });
  }

  /** Same site: the place's own lead folds into the candidate. */
  merge(card: AgentRecommendation): void {
    if (card.referenceId == null) return;
    this.run(card, () => this.http.post(`/api/leads/links/${card.referenceId}/accept`, {}), {
      403: 'You do not have permission to merge leads.',
      fallback: 'Could not merge these records.',
    });
  }

  /** Two different businesses: the suggestion is rejected, never re-offered. */
  keepSeparate(card: AgentRecommendation): void {
    if (card.referenceId == null) return;
    this.run(card, () => this.http.post(`/api/leads/links/${card.referenceId}/reject`, {}), {
      403: 'You do not have permission to decide this match.',
      fallback: 'Could not reject this match.',
    });
  }

  /** Let the exception out: InternalReview → Approved, so it can be sent. */
  confirmQuote(card: AgentRecommendation, approve: boolean): void {
    if (card.referenceId == null || !this.reason.trim()) return;
    const body = { approve, reason: this.reason.trim() };
    this.pending = null;
    this.reason = '';
    this.run(card, () => this.http.post(`/api/quotes/${card.referenceId}/approve`, body), {
      403: 'You do not have permission to decide quote pricing.',
      fallback: approve ? 'Could not approve the quote.' : 'Could not send the quote back.',
    });
  }

  /** One command shape for every card: guard the double-click, run, report the
   *  server's own refusal, reload. The call is a factory so a card already in
   *  flight does not even build the second request. */
  private run(
    card: AgentRecommendation,
    call: () => Observable<unknown>,
    messages: Record<number | 'fallback', string>,
  ): void {
    const key = this.cardKey(card);
    if (this.busy.has(key)) return;
    this.busy.add(key);
    this.actionError = null;
    call().subscribe({
      next: () => {
        this.busy.delete(key);
        this.acted.emit();
        this.load();
      },
      error: err => {
        this.busy.delete(key);
        this.actionError =
          err?.error?.error ?? messages[err?.status as number] ?? messages.fallback;
      },
    });
  }

  // ── Navigation for the cards this page does not decide ──────────────────

  openCard(card: AgentRecommendation): void {
    if (card.kind === 'Approval') {
      this.router.navigate(['/leads']);
      return;
    }
    if (card.kind === 'IdentityReview') {
      this.router.navigate(['/leads'], { queryParams: { status: 'Review' } });
      return;
    }
    this.router.navigate(['/leads']);
  }

  /** The action word each card type deserves. */
  openLabel(card: AgentRecommendation): string {
    switch (card.kind) {
      case 'StartPursuit': return 'Open';
      case 'GuardrailWarning': return 'Re-price';
      case 'Approval': return 'Read the payload';
      case 'QuoteException': return 'Open the case';
      default: return 'Review';
    }
  }

  waitingDays(card: AgentRecommendation): number | null {
    if (!card.sinceUtc) return null;
    const days = Math.floor((Date.now() - new Date(card.sinceUtc).getTime()) / 86400000);
    return days > 0 ? days : null;
  }
}
